import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { ORDER_STATUSES, PAYMENT_STATUSES } from '../utils/constants';
import { getImageUrl } from '../utils/imageHelper';
import Loading from '../components/common/Loading';
import './OrderDetail.css';

const OrderDetail = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: '',
    payment_status: '',
  });

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const orderData = await orderService.getOrderById(orderId);
      setOrder(orderData);
      setStatusForm({
        status: orderData.status || '',
        payment_status: orderData.payment_status || '',
      });
    } catch (err) {
      let errorMessage = 'Failed to load order';
      if (err.response?.status === 404) {
        errorMessage = 'Order not found';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await orderService.updateOrderStatus(orderId, statusForm);
      await loadOrder(); // Reload order to get updated data
      alert('Order status updated successfully');
    } catch (err) {
      alert('Failed to update order status: ' + (err.message || 'Unknown error'));
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price) => {
    return `Rs. ${parseFloat(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#fbbc04',
      confirmed: '#4285f4',
      processing: '#9334e6',
      shipped: '#34a853',
      delivered: '#137333',
      cancelled: '#ea4335',
      paid: '#34a853',
      failed: '#ea4335',
    };
    return colors[status] || '#9aa0a6';
  };

  if (loading) {
    return <Loading message="Loading order details..." />;
  }

  if (error) {
    return (
      <div className="order-detail-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <Link to="/orders" className="back-link">
            ← Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-detail-container">
        <div className="error-message">
          <h2>Order not found</h2>
          <Link to="/orders" className="back-link">
            ← Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-container">
      <div className="order-detail-header">
        <Link to="/orders" className="back-link">
          ← Back to Orders
        </Link>
        <h1 className="page-title">Order Details</h1>
      </div>

      <div className="order-detail-content">
        {/* Order Info Section */}
        <div className="order-section">
          <h2>Order Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Order Number:</label>
              <span>{order.order_number}</span>
            </div>
            <div className="info-item">
              <label>Order Date:</label>
              <span>{formatDate(order.created_at)}</span>
            </div>
            <div className="info-item">
              <label>Order Status:</label>
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {formatStatus(order.status)}
              </span>
            </div>
            <div className="info-item">
              <label>Payment Status:</label>
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.payment_status) }}
              >
                {formatStatus(order.payment_status)}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Info Section */}
        <div className="order-section">
          <h2>Customer Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Name:</label>
              <span>{order.user?.full_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{order.user?.email || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Shipping Info Section */}
        <div className="order-section">
          <h2>Shipping Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name:</label>
              <span>{order.shipping_full_name}</span>
            </div>
            <div className="info-item">
              <label>Address:</label>
              <span>{order.shipping_address}</span>
            </div>
            <div className="info-item">
              <label>City:</label>
              <span>{order.shipping_city || '—'}</span>
            </div>
            <div className="info-item">
              <label>District:</label>
              <span>{order.shipping_district_name || '—'}</span>
            </div>
            <div className="info-item">
              <label>Province:</label>
              <span>{order.shipping_province_name || '—'}</span>
            </div>
            <div className="info-item">
              <label>Phone:</label>
              <span>{order.shipping_phone}</span>
            </div>
            <div className="info-item">
              <label>Shipping Method:</label>
              <span>{formatStatus(order.shipping_method)}</span>
            </div>
          </div>
        </div>

        {/* Order Items Section */}
        <div className="order-section">
          <h2>Order Items</h2>
          <div className="order-items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item) => {
                const primaryImage = item.product?.images?.find((img) => img.is_primary) || item.product?.images?.[0];
                return (
                  <div key={item.id} className="order-item-card">
                    {primaryImage && (
                      <div className="item-image">
                        <img
                          src={getImageUrl(primaryImage.image_url) || '/placeholder.png'}
                          alt={primaryImage.alt_text || item.product_name}
                        />
                      </div>
                    )}
                    <div className="item-details">
                      <h4>{item.product_name}</h4>
                      {item.color_name && (
                        <p className="item-color">
                          <span
                            style={{
                              display: 'inline-block',
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: item.color_hex,
                              border: '1px solid rgba(0,0,0,0.15)',
                              marginRight: 6,
                              verticalAlign: 'middle',
                            }}
                            aria-hidden="true"
                          />
                          Color: {item.color_name}
                        </p>
                      )}
                      <p className="item-price">Price: {formatPrice(item.product_price)}</p>
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                      <p className="item-subtotal">Subtotal: {formatPrice(item.subtotal)}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p>No items found</p>
            )}
          </div>
        </div>

        {/* Order Summary Section */}
        <div className="order-section">
          <h2>Order Summary</h2>
          <div className="order-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping Cost:</span>
              <span>{formatPrice(order.shipping_cost)}</span>
            </div>
            {parseFloat(order.cod_surcharge || 0) > 0 && (
              <div className="summary-row">
                <span>COD Surcharge:</span>
                <span>{formatPrice(order.cod_surcharge)}</span>
              </div>
            )}
            <div className="summary-row total-row">
              <span>Total:</span>
              <span>{formatPrice(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Payment Info Section */}
        <div className="order-section">
          <h2>Payment Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Payment Method:</label>
              <span>{formatStatus(order.payment_method)}</span>
            </div>
          </div>
        </div>

        {/* Update Status Section */}
        <div className="order-section">
          <h2>Update Order Status</h2>
          <form onSubmit={handleStatusUpdate} className="status-form">
            <div className="form-group">
              <label htmlFor="status">Order Status:</label>
              <select
                id="status"
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                required
              >
                <option value="">Select status</option>
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="payment_status">Payment Status:</label>
              <select
                id="payment_status"
                value={statusForm.payment_status}
                onChange={(e) => setStatusForm({ ...statusForm, payment_status: e.target.value })}
                required
              >
                <option value="">Select payment status</option>
                {PAYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="update-button" disabled={updating}>
              {updating ? 'Updating...' : 'Update Status'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
